import { useEffect, useState } from "react";
import { Users, TrendingUp, Clock, IndianRupee } from "lucide-react";
import api, { fmtINR, shortDate } from "@/lib/api";
import PaidViaWidget from "@/components/PaidViaWidget";
import PlatformHealthWidget from "@/components/PlatformHealthWidget";

function Stat({ icon: Icon, label, value, hint, color = "blue" }) {
    const map = { blue: "bg-blue-50 text-blue-700", emerald: "bg-emerald-50 text-emerald-700",
                  amber: "bg-amber-50 text-amber-700", slate: "bg-slate-100 text-slate-700" };
    return (
        <div className="card p-5 card-hover">
            <div className="flex items-start justify-between">
                <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</div>
                <div className={`w-8 h-8 rounded-md flex items-center justify-center ${map[color]}`}><Icon className="w-4 h-4" /></div>
            </div>
            <div className="text-3xl font-bold mt-2 font-mono tracking-tight">{value}</div>
            {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
        </div>
    );
}

export default function AdminDashboard() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/admin/dashboard").then((r) => setData(r.data)); }, []);
    if (!data) return <div className="text-slate-500">Loading…</div>;
    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
                <p className="text-sm text-slate-500 mt-1">Overview of your payment gateway</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat icon={Users} label="Total Partners" value={data.partners_total} hint={`${data.partners_active} active`} color="blue" />
                <Stat icon={TrendingUp} label="Today's Transactions" value={data.today_count} hint={`${data.today_success_count} successful`} color="emerald" />
                <Stat icon={IndianRupee} label="Today's Volume" value={fmtINR(data.today_volume)} hint="Successful only" color="slate" />
                <Stat icon={Clock} label="Pending" value={data.pending_count} hint="Awaiting verification" color="amber" />
            </div>
            <PlatformHealthWidget />
            <div className="grid lg:grid-cols-2 gap-4">
                <div className="card p-5">
                    <h3 className="font-semibold mb-3">Recent transactions</h3>
                    <div className="space-y-1">
                        {data.recent_transactions.length === 0 && <div className="text-sm text-slate-400 text-center py-8">No transactions yet.</div>}
                        {data.recent_transactions.map((t) => (
                            <div key={t.txn_id} className="flex items-center justify-between py-2 border-b last:border-0 border-slate-100">
                                <div className="min-w-0">
                                    <div className="text-sm font-medium truncate">{t.customer_username}</div>
                                    <div className="text-xs text-slate-500">{shortDate(t.created_at)} · {t.channel_type}</div>
                                </div>
                                <div className="text-right">
                                    <div className="font-mono text-sm">{fmtINR(t.amount)}</div>
                                    <span className={`pill ${t.status === "success" ? "pill-success" : t.status === "submitted" || t.status === "pending" ? "pill-pending" : "pill-failed"}`}>{t.status}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="card p-5">
                    <h3 className="font-semibold mb-3">Partner activity</h3>
                    <div className="text-sm text-slate-500">
                        {data.partners_total === 0 ? "No partners onboarded yet." : `${data.partners_active} of ${data.partners_total} partners are active.`}
                    </div>
                </div>
            </div>
            <div className="grid lg:grid-cols-2 gap-4">
                <PaidViaWidget scope="admin" />
            </div>
        </div>
    );
}
